import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Hls from 'hls.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTriangleExclamation } from '@/fontawesome';

type Status = 'connecting' | 'playing' | 'error';

export default function DayTradingLivestreamStandalonePage() {
  const [searchParams] = useSearchParams();
  const viewerUrl = searchParams.get('url') ?? '';

  const videoRef = useRef<HTMLVideoElement>(null);
  const [status, setStatus] = useState<Status>('connecting');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    document.title = 'Livestream';
  }, []);

  useEffect(() => {
    if (viewerUrl === '') {
      setStatus('error');
      setErrorMessage('Kein Stream-Link angegeben.');
      return;
    }
    const video = videoRef.current;
    if (video === null) return;

    setStatus('connecting');
    setErrorMessage('');

    let cancelled = false;
    let hlsInstance: Hls | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    if (Hls.isSupported()) {
      const scheduleRetry = () => {
        if (cancelled) return;
        if (hlsInstance !== null) {
          hlsInstance.destroy();
          hlsInstance = null;
        }
        attempt += 1;
        const delay = Math.min(3000, 1000 * 2 ** (attempt - 1));
        setStatus('error');
        setErrorMessage(`Verbindung zum Livestream verloren –\nneuer Versuch in ${Math.round(delay / 1000)}s…`);
        retryTimer = setTimeout(() => {
          if (cancelled) return;
          startHls();
        }, delay);
      };

      const startHls = () => {
        if (cancelled) return;
        setStatus('connecting');
        setErrorMessage('');
        const hls = new Hls({
          lowLatencyMode: true,
          liveSyncDuration: 1.5,
          liveMaxLatencyDuration: 4,
          maxLiveSyncPlaybackRate: 1.5,
          backBufferLength: 4,
          maxBufferLength: 4,
        });
        hlsInstance = hls;

        hls.on(Hls.Events.ERROR, (_evt, data) => {
          if (cancelled) return;
          if (data.fatal !== true) return;
          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
            return;
          }
          scheduleRetry();
        });

        hls.loadSource(viewerUrl);
        hls.attachMedia(video);
      };

      startHls();

      const onPlaying = () => {
        attempt = 0;
        setStatus('playing');
      };
      const onWaiting = () => setStatus('connecting');
      const onCanPlay = () => {
        video.play().catch(() => {});
      };
      const onPause = () => {
        video.play().catch(() => {});
      };
      video.addEventListener('canplay', onCanPlay, { once: true });
      video.addEventListener('playing', onPlaying);
      video.addEventListener('waiting', onWaiting);
      video.addEventListener('pause', onPause);

      return () => {
        cancelled = true;
        if (retryTimer !== null) clearTimeout(retryTimer);
        video.removeEventListener('canplay', onCanPlay);
        video.removeEventListener('playing', onPlaying);
        video.removeEventListener('waiting', onWaiting);
        video.removeEventListener('pause', onPause);
        if (hlsInstance !== null) hlsInstance.destroy();
      };
    }

    video.src = viewerUrl;
    const onCanPlay = () => {
      video.play().catch(() => {});
    };
    const onPlaying = () => setStatus('playing');
    const onWaiting = () => setStatus('connecting');
    const onError = () => {
      setStatus('error');
      setErrorMessage('Livestream konnte nicht abgespielt werden.');
    };
    const onPause = () => {
      video.play().catch(() => {});
    };
    video.addEventListener('canplay', onCanPlay, { once: true });
    video.addEventListener('playing', onPlaying);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('error', onError);
    video.addEventListener('pause', onPause);

    return () => {
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('error', onError);
      video.removeEventListener('pause', onPause);
      video.removeAttribute('src');
      video.load();
    };
  }, [viewerUrl]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    const noop = () => {};
    const actions: MediaSessionAction[] = ['pause', 'stop', 'seekbackward', 'seekforward', 'seekto', 'previoustrack', 'nexttrack'];
    actions.forEach((action) => {
      try {
        navigator.mediaSession.setActionHandler(action, noop);
      } catch {
        // unsupported action — ignore
      }
    });
    return () => {
      actions.forEach((action) => {
        try {
          navigator.mediaSession.setActionHandler(action, null);
        } catch {
          // unsupported action — ignore
        }
      });
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center">
      <video
        ref={videoRef}
        className="w-full h-full"
        playsInline
        autoPlay
        muted
        controls
        disablePictureInPicture
        disableRemotePlayback
        controlsList="nodownload noplaybackrate"
      />
      {status === 'connecting' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 text-white text-sm pointer-events-none">
          <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 animate-spin" />
          <span>Stream wird geladen…</span>
        </div>
      )}
      {status === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/80 text-white text-sm text-center px-4 pointer-events-none">
          <FontAwesomeIcon icon={faTriangleExclamation} className="w-6 h-6 text-amber-400" />
          <span className="whitespace-pre-line">{errorMessage !== '' ? errorMessage : 'Livestream nicht verfügbar.'}</span>
        </div>
      )}
    </div>
  );
}
